import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useEditor, EditorContent, type Editor, type JSONContent } from '@tiptap/react';
import type { ChatDoc } from '../types/chat';
import type { RichEditorImageUploadHandler } from '../../rich-editor/types';
import type { DocNode } from './DocContent';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorToolbar, LinkPopover } from './EditorToolbar';
import { sanitizeHtml } from './HtmlContent';
import { useChatConfig } from '../context/ChatConfigContext';
import { chatEditorExtensions } from '../utils/chatEditorExtensions';
import { useSendOnEnter } from '../hooks/useSendOnEnter';
import { isSendKeystroke, withSendKeyHint } from '../utils/sendOnEnter';
import {
    editableDoc,
    isAllowedGifUrl,
    routeComposerFiles,
    withDraftImagePlaceholders
} from '../utils/inlineImages';
import { useInlineImageUpload } from '../hooks/useInlineImageUpload';
import { InlineImageStatus } from './InlineImageStatus';

const DEFAULT_INPUT_PLACEHOLDER = 'Type a message...';

const EmojiPicker = lazy(() =>
    import('@emoji-mart/react').then((mod) => ({ default: mod.default }))
);
const GifPicker = lazy(() => import('./GifPicker').then((mod) => ({ default: mod.default })));

export interface EditingMessageAttachment {
    patientFileId: string;
    name: string;
}

export interface EditingMessage {
    id: string;
    html: string;
    /** The v2 body; loaded instead of `html` so inline images survive the edit. */
    doc?: DocNode;
    existingAttachments?: EditingMessageAttachment[];
}

interface ChatInputProps {
    /** `html` goes out to Monday; `doc` is what gets stored, absent on an HTML-only edit. */
    onSubmit: (html: string, doc?: ChatDoc) => void;
    pendingFiles: File[];
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onAddFiles: (files: File[]) => void;
    onRemoveFile: (e: React.MouseEvent<HTMLButtonElement>) => void;
    sending: boolean;
    isSearchOpen: boolean;
    onToggleSearch: () => void;
    editingMessage?: EditingMessage | null;
    onCancelEdit?: () => void;
    onRemoveExistingAttachment?: (patientFileId: string) => void;
    draftKey?: string;
    trailingActions?: React.ReactNode;
    /** Toolbar content beside emoji/GIF; `insertContent` puts TipTap nodes at the caret. */
    renderTools?: (insertContent: (content: JSONContent[]) => void) => React.ReactNode;
    /** Resolve false to keep the draft instead of sending. */
    confirmSubmit?: (text: string) => Promise<boolean>;
    /** Enables inline images (toolbar, paste, drop); without it every file goes to the tray. */
    onImageUpload?: RichEditorImageUploadHandler;
}

const DRAFT_WRITE_DEBOUNCE_MS = 300;
const DRAFT_MAX_BYTES = 500_000;

let draftPersistenceWarned = false;
const warnDraftFailure = (err: unknown): void => {
    if (draftPersistenceWarned) return;
    draftPersistenceWarned = true;
    // eslint-disable-next-line no-console
    console.warn('ChatInput: draft persistence unavailable', err);
};

const readDraft = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch (err) {
        warnDraftFailure(err);
        return null;
    }
};

const writeDraft = (key: string, draft: string): void => {
    if (draft.length > DRAFT_MAX_BYTES) return;
    try {
        localStorage.setItem(key, draft);
    } catch (err) {
        warnDraftFailure(err);
    }
};

const parseDraft = (saved: string): JSONContent | string => {
    try {
        const doc = JSON.parse(saved) as JSONContent;
        if (doc?.type === 'doc') return withDraftImagePlaceholders(doc) as JSONContent;
    } catch {
        // Drafts saved before the doc format are HTML.
    }
    return sanitizeHtml(saved);
};

const draftJson = (editor: Editor): string =>
    JSON.stringify(withDraftImagePlaceholders(editor.getJSON()));

const clearDraft = (key: string): void => {
    try {
        localStorage.removeItem(key);
    } catch {
        /* noop */
    }
};

const SEND_ON_ENTER_KEY = 'chatInputSendOnEnter';

interface FileRouting {
    routeFiles: (files: File[]) => boolean;
    importPastedHtml: (html: string, at: { from: number; to: number }) => boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
    onSubmit,
    pendingFiles,
    onFileChange,
    onAddFiles,
    onRemoveFile,
    sending,
    isSearchOpen,
    onToggleSearch,
    editingMessage,
    onCancelEdit,
    onRemoveExistingAttachment,
    draftKey,
    trailingActions,
    renderTools,
    confirmSubmit,
    onImageUpload
}) => {
    const [isEmpty, setIsEmpty] = useState(true);
    const [showLinkPopover, setShowLinkPopover] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const { sendOnEnter, toggleSendOnEnter } = useSendOnEnter(SEND_ON_ENTER_KEY, true);
    const sendOnEnterRef = useRef<boolean>(sendOnEnter);
    const submitRef = useRef<() => void>(() => {});

    const { inputPlaceholder, resolveFileUrl } = useChatConfig();
    // Latest value without re-running the edit load: it changes on every feed poll.
    const resolveFileUrlRef = useRef(resolveFileUrl);
    resolveFileUrlRef.current = resolveFileUrl;
    const inlineImages = useInlineImageUpload(onImageUpload);
    // Read by the paste/drop handlers, which the editor creates once.
    const fileRoutingRef = useRef<FileRouting | null>(null);
    const inputPlaceholderRef = useRef<string>(inputPlaceholder ?? DEFAULT_INPUT_PLACEHOLDER);
    useEffect(() => {
        inputPlaceholderRef.current = inputPlaceholder ?? DEFAULT_INPUT_PLACEHOLDER;
    }, [inputPlaceholder]);

    useEffect(() => {
        sendOnEnterRef.current = sendOnEnter;
    }, [sendOnEnter]);

    const draftKeyRef = useRef<string | undefined>(draftKey);
    const isEditingRef = useRef<boolean>(!!editingMessage);
    const draftWriteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingDraftWriteRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        draftKeyRef.current = draftKey;
    }, [draftKey]);
    useEffect(() => {
        isEditingRef.current = !!editingMessage;
    }, [editingMessage]);

    // Flush rather than drop a debounced write: a host that remounts the input
    // (e.g. a thread switch) would otherwise lose the last keystrokes.
    useEffect(() => {
        return () => {
            if (draftWriteTimerRef.current) clearTimeout(draftWriteTimerRef.current);
            pendingDraftWriteRef.current?.();
        };
    }, []);

    useEffect(() => {
        const anyPickerOpen = showEmojiPicker || showGifPicker || showLinkPopover;
        if (!anyPickerOpen && !editingMessage) return;

        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.stopImmediatePropagation();
                if (anyPickerOpen) {
                    setShowEmojiPicker(false);
                    setShowGifPicker(false);
                    setShowLinkPopover(false);
                } else if (editingMessage) {
                    onCancelEdit?.();
                }
            }
        };
        document.addEventListener('keydown', handler, true);
        return () => document.removeEventListener('keydown', handler, true);
    }, [showEmojiPicker, showGifPicker, showLinkPopover, editingMessage, onCancelEdit]);

    const editor = useEditor({
        extensions: [
            ...chatEditorExtensions(),
            // Placeholder is a function so it re-reads the send-mode ref on every
            // decoration rebuild. Paired with the useEffect below that dispatches a
            // no-op transaction when `sendOnEnter` flips, this gives instant updates
            // without recreating the editor (which would drop content/selection/draft state).
            Placeholder.configure({
                placeholder: () =>
                    withSendKeyHint(inputPlaceholderRef.current, sendOnEnterRef.current)
            })
        ],
        editable: !sending,
        onUpdate: ({ editor: e }) => {
            setIsEmpty(e.isEmpty);
            const key = draftKeyRef.current;
            if (!key || isEditingRef.current) return;
            if (draftWriteTimerRef.current) clearTimeout(draftWriteTimerRef.current);
            const persist = () => {
                pendingDraftWriteRef.current = null;
                if (e.isEmpty) {
                    clearDraft(key);
                } else {
                    writeDraft(key, draftJson(e));
                }
            };
            pendingDraftWriteRef.current = persist;
            draftWriteTimerRef.current = setTimeout(persist, DRAFT_WRITE_DEBOUNCE_MS);
        },
        editorProps: {
            handleKeyDown: (_view, event) => {
                if (!isSendKeystroke(event, sendOnEnterRef.current)) return false;
                event.preventDefault();
                submitRef.current();
                return true;
            },
            handlePaste: (view, event) => {
                const files = Array.from(event.clipboardData?.items ?? [])
                    .filter((item) => item.kind === 'file')
                    .map((item) => item.getAsFile())
                    .filter((file): file is File => file !== null);
                const { from, to } = view.state.selection;
                const html = event.clipboardData?.getData('text/html') ?? '';
                const routing = fileRoutingRef.current;
                if (!routing?.routeFiles(files) && !routing?.importPastedHtml(html, { from, to })) {
                    return false;
                }
                event.preventDefault();
                return true;
            },
            handleDrop: (_view, event) => {
                const files = event.dataTransfer?.files;
                if (!files || files.length === 0) return false;
                event.preventDefault();
                fileRoutingRef.current?.routeFiles(Array.from(files));
                return true;
            },
            attributes: {
                class: 'prose-chat'
            }
        }
    });

    // Nudge the editor to rebuild decorations so the Placeholder function
    // re-evaluates with the new send-mode. An empty transaction is the
    // standard ProseMirror way to trigger plugin recomputation without
    // touching document content, selection, or marks.
    useEffect(() => {
        if (!editor) return;
        editor.view.dispatch(editor.state.tr);
    }, [editor, sendOnEnter]);

    const prevEditIdRef = useRef<string | null>(null);
    const editIsHtmlOnlyRef = useRef(false);
    useEffect(() => {
        if (!editor) return;
        if (editingMessage && editingMessage.id !== prevEditIdRef.current) {
            const doc = editableDoc(editor.schema, editingMessage.doc, resolveFileUrlRef.current);
            editIsHtmlOnlyRef.current = !doc;
            editor.commands.setContent(doc ?? sanitizeHtml(editingMessage.html));
            setIsEmpty(editor.isEmpty);
            prevEditIdRef.current = editingMessage.id;
            setTimeout(() => editor.commands.focus('end'), 0);
        } else if (!editingMessage && prevEditIdRef.current) {
            editor.commands.clearContent();
            setIsEmpty(true);
            prevEditIdRef.current = null;
            editIsHtmlOnlyRef.current = false;
        }
    }, [editingMessage, editor]);

    // undefined sentinel so first mount is treated as a key change (triggers restore).
    const prevDraftKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!editor || editingMessage) return;
        const prevKey = prevDraftKeyRef.current;
        if (draftKey === prevKey) return;

        if (draftWriteTimerRef.current) {
            clearTimeout(draftWriteTimerRef.current);
            draftWriteTimerRef.current = null;
        }
        pendingDraftWriteRef.current = null;
        if (prevKey && !editor.isEmpty) {
            writeDraft(prevKey, draftJson(editor));
        }
        prevDraftKeyRef.current = draftKey;

        if (!draftKey) return;
        const saved = readDraft(draftKey);
        if (saved) {
            editor.commands.setContent(parseDraft(saved));
        } else if (!editor.isEmpty) {
            editor.commands.clearContent();
        }
        setIsEmpty(editor.isEmpty);
    }, [editor, draftKey, editingMessage]);

    const handleSubmit = useCallback(async () => {
        if (!editor) return;
        if (editor.isEmpty && pendingFiles.length === 0) return;
        if (sending || inlineImages.isUploading) return;
        if (confirmSubmit && !(await confirmSubmit(editor.getText()))) return;
        onSubmit(
            sanitizeHtml(editor.getHTML()),
            editIsHtmlOnlyRef.current ? undefined : editor.getJSON()
        );
        editor.commands.clearContent();
        setIsEmpty(true);
        if (draftKey) {
            if (draftWriteTimerRef.current) clearTimeout(draftWriteTimerRef.current);
            pendingDraftWriteRef.current = null;
            clearDraft(draftKey);
        }
    }, [
        editor,
        onSubmit,
        pendingFiles.length,
        sending,
        inlineImages.isUploading,
        draftKey,
        confirmSubmit
    ]);

    submitRef.current = handleSubmit;

    const routeFiles = (files: File[]): boolean => {
        const { inline, tray } = routeComposerFiles(files, inlineImages.enabled);
        if (tray.length > 0) onAddFiles(tray);
        if (inline.length > 0 && editor) void inlineImages.insertFiles(editor, inline);
        return inline.length + tray.length > 0;
    };
    fileRoutingRef.current = {
        routeFiles,
        importPastedHtml: (html, at) =>
            inlineImages.enabled && !!editor && inlineImages.importHtml(editor, html, at)
    };

    const insertContent = useCallback(
        (content: JSONContent[]) => {
            editor?.chain().focus().insertContent(content).run();
        },
        [editor]
    );

    const canSend = !sending && !inlineImages.isUploading && (!isEmpty || pendingFiles.length > 0);

    return (
        <>
            <div className="relative bg-white border-t border-gray-200 flex-shrink-0 pb-2">
                {/* Floating pickers */}
                {showEmojiPicker && editor && (
                    <div className="absolute bottom-full left-3 z-10 pb-2">
                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                            <Suspense
                                fallback={
                                    <div className="h-[350px] w-[352px] flex items-center justify-center">
                                        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                                    </div>
                                }
                            >
                                <EmojiPicker
                                    data={async () => (await import('@emoji-mart/data')).default}
                                    onEmojiSelect={(emoji: any) => {
                                        editor.chain().focus().insertContent(emoji.native).run();
                                        setShowEmojiPicker(false);
                                    }}
                                    theme="light"
                                    previewPosition="none"
                                    skinTonePosition="search"
                                    maxFrequentRows={1}
                                />
                            </Suspense>
                        </div>
                    </div>
                )}
                {showGifPicker && editor && (
                    <div className="absolute bottom-full left-3 z-10 pb-2">
                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden w-[340px]">
                            <Suspense
                                fallback={
                                    <div className="h-[320px] flex items-center justify-center">
                                        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                                    </div>
                                }
                            >
                                <GifPicker
                                    onSelect={(url) => {
                                        if (!isAllowedGifUrl(url)) return;
                                        editor
                                            .chain()
                                            .focus()
                                            .setImage({ src: url, alt: 'GIF' })
                                            .run();
                                        setShowGifPicker(false);
                                    }}
                                />
                            </Suspense>
                        </div>
                    </div>
                )}
                {showLinkPopover && editor && (
                    <div className="absolute bottom-full left-3 z-10 pb-2 w-[300px]">
                        <LinkPopover
                            initialUrl={editor.getAttributes('link').href ?? ''}
                            initialLabel={(() => {
                                const { from, to } = editor.state.selection;
                                return from !== to ? editor.state.doc.textBetween(from, to) : '';
                            })()}
                            onSubmit={(url, label) => {
                                const trimmedUrl = url.trim();
                                const urlObj = (() => {
                                    try {
                                        return new URL(trimmedUrl);
                                    } catch {
                                        return null;
                                    }
                                })();
                                if (
                                    urlObj &&
                                    !['http:', 'https:', 'mailto:'].includes(urlObj.protocol)
                                )
                                    return;

                                const { from, to } = editor.state.selection;
                                const hasSelection = from !== to;
                                if (hasSelection) {
                                    editor
                                        .chain()
                                        .focus()
                                        .extendMarkRange('link')
                                        .setLink({ href: trimmedUrl })
                                        .run();
                                    if (label && label !== editor.state.doc.textBetween(from, to)) {
                                        editor
                                            .chain()
                                            .focus()
                                            .deleteSelection()
                                            .insertContent(label)
                                            .setTextSelection({
                                                from: editor.state.selection.from - label.length,
                                                to: editor.state.selection.from
                                            })
                                            .setLink({ href: trimmedUrl })
                                            .run();
                                    }
                                } else {
                                    const linkText = label || trimmedUrl;
                                    editor
                                        .chain()
                                        .focus()
                                        .insertContent(linkText)
                                        .setTextSelection({
                                            from: editor.state.selection.from - linkText.length,
                                            to: editor.state.selection.from
                                        })
                                        .setLink({ href: trimmedUrl })
                                        .run();
                                }
                                editor.chain().focus().unsetMark('link').run();
                                setShowLinkPopover(false);
                            }}
                            onRemove={() => {
                                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                                setShowLinkPopover(false);
                            }}
                            onClose={() => setShowLinkPopover(false)}
                        />
                    </div>
                )}

                {/* Edit mode banner */}
                {editingMessage && (
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                            {sending ? (
                                <>
                                    <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                    <span className="text-xs font-medium text-gray-500">
                                        Saving...
                                    </span>
                                </>
                            ) : (
                                <>
                                    <PencilSquareIcon className="w-3.5 h-3.5 text-gray-500" />
                                    <span className="text-xs font-medium text-gray-600">
                                        Editing message
                                    </span>
                                </>
                            )}
                        </div>
                        {!sending && (
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-0.5 rounded transition-colors"
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                )}

                <div className="px-3">
                    <div className="tiptap-editor">
                        <EditorContent editor={editor} />
                    </div>
                    <InlineImageStatus
                        uploading={inlineImages.isUploading}
                        error={inlineImages.error}
                        onDismiss={inlineImages.dismissError}
                    />
                    <EditorToolbar
                        editor={editor}
                        linkPopoverOpen={showLinkPopover}
                        onToggleLinkPopover={() => {
                            setShowLinkPopover((v) => !v);
                            setShowEmojiPicker(false);
                            setShowGifPicker(false);
                        }}
                        onInsertImages={inlineImages.enabled ? routeFiles : undefined}
                        emojiPickerOpen={showEmojiPicker}
                        onToggleEmojiPicker={() => {
                            setShowEmojiPicker((v) => !v);
                            setShowLinkPopover(false);
                            setShowGifPicker(false);
                        }}
                        gifPickerOpen={showGifPicker}
                        onToggleGifPicker={() => {
                            setShowGifPicker((v) => !v);
                            setShowEmojiPicker(false);
                            setShowLinkPopover(false);
                        }}
                        isSearchOpen={isSearchOpen}
                        onToggleSearch={onToggleSearch}
                        onFileChange={onFileChange}
                        sending={sending}
                        canSend={canSend}
                        onSend={handleSubmit}
                        sendOnEnter={sendOnEnter}
                        onToggleSendOnEnter={toggleSendOnEnter}
                        trailingActions={trailingActions}
                        tools={renderTools?.(insertContent)}
                    />
                </div>
            </div>

            {editingMessage && (editingMessage.existingAttachments?.length ?? 0) > 0 && (
                <div className="bg-white px-3 pb-2 -mt-1 flex-shrink-0 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2 pt-2">
                        {editingMessage.existingAttachments!.map((a) => (
                            <span
                                key={a.patientFileId}
                                className="inline-flex items-center gap-1 max-w-[200px] truncate px-2 py-1 rounded-full text-[11px] bg-gray-50 border border-gray-200 text-gray-700"
                            >
                                <span className="truncate" title={a.name}>
                                    {a.name}
                                </span>
                                <button
                                    type="button"
                                    className="ml-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => onRemoveExistingAttachment?.(a.patientFileId)}
                                    disabled={sending || !onRemoveExistingAttachment}
                                    aria-label={`Remove ${a.name}`}
                                >
                                    <XMarkIcon className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {pendingFiles.length > 0 && (
                <div className="bg-white px-3 pb-2 -mt-1 flex-shrink-0 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2 pt-2">
                        {pendingFiles.map((f, idx) => (
                            <span
                                key={`${f.name}-${f.size}-${idx}`}
                                className="inline-flex items-center gap-1 max-w-[200px] truncate px-2 py-1 rounded-full text-[11px] bg-gray-50 border border-gray-200 text-gray-700"
                            >
                                <span className="truncate" title={f.name}>
                                    {f.name}
                                </span>
                                <button
                                    type="button"
                                    className="ml-1 text-gray-500 hover:text-gray-700"
                                    data-index={idx}
                                    onClick={onRemoveFile}
                                    aria-label={`Remove ${f.name}`}
                                >
                                    <XMarkIcon className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export { ChatInput };
